import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router';
import { useAuthStore } from '../../store/authStore';
import { useFolders } from '../../hooks/useFolders';
import { Button } from '../ui/Button';
import { SearchModal } from './SearchModal';

export function AppLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: folders } = useFolders();

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-surface-0 text-text-primary overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 border-r border-surface-border bg-surface-1 flex flex-col">
        <div className="h-14 flex items-center justify-between px-4 border-b border-surface-border">
          <span className="font-bold text-lg bg-clip-text text-transparent bg-gradient-to-r from-brand-400 to-brand-600">
            JSON Workspace
          </span>
          <button 
            onClick={() => setIsSearchOpen(true)}
            className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-2 rounded-md transition-colors"
            title="Search (Cmd+K)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <Link
            to="/app/dashboard"
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors mb-1 ${
              location.pathname === '/app/dashboard'
                ? 'bg-brand-500/10 text-brand-400'
                : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
            }`}
          >
            Dashboard
          </Link>
          <Link
            to="/app/trash"
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              location.pathname === '/app/trash'
                ? 'bg-error/10 text-error'
                : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Trash
          </Link>
          
          <div className="pt-4 pb-2 flex items-center justify-between px-3">
            <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
              Folders
            </span>
            <button className="text-text-muted hover:text-brand-400" title="New Folder">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-1">
            {folders?.length === 0 ? (
              <div className="px-3 py-2 text-xs text-text-muted italic">
                No folders yet.
              </div>
            ) : (
              folders?.map(folder => (
                <Link
                  key={folder.id}
                  to={`/app/dashboard?folderId=${folder.id}`}
                  className="flex items-center px-3 py-1.5 rounded-md text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors group"
                >
                  <svg className="w-4 h-4 mr-2 text-text-muted group-hover:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  {folder.name}
                </Link>
              ))
            )}
          </div>
        </nav>

        <div className="p-4 border-t border-surface-border">
          <div className="flex items-center justify-between">
            <div className="flex flex-col truncate">
              <span className="text-sm font-medium truncate">{user?.name}</span>
              <span className="text-xs text-text-muted truncate">{user?.email}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="px-2" title="Log out">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Outlet />
      </main>

      {/* Global Modals */}
      {isSearchOpen && <SearchModal onClose={() => setIsSearchOpen(false)} />}
    </div>
  );
}
